import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatViewsAr, type HomeVideo } from "@/data/home-videos";

type Ctx = { play: (v: HomeVideo) => void; open: boolean };
const PlayerCtx = createContext<Ctx>({ play: () => {}, open: false });
export const useVideoPlayer = () => useContext(PlayerCtx);

const btn = "inline-flex min-h-12 flex-1 items-center justify-center whitespace-nowrap rounded-ds-pill px-4 font-display text-[15px] font-bold transition-colors duration-ds-fast";

/** One player for the whole home page: Dialog from md up, bottom sheet below. The iframe exists only while open. */
export function VideoPlayerProvider({ children }: { children: ReactNode }) {
  const [video, setVideo] = useState<HomeVideo | null>(null);
  const [open, setOpen] = useState(false);
  const mobile = useIsMobile();
  const trigger = useRef<HTMLElement | null>(null);
  const restoreFocus = useCallback((e: Event) => {
    const el = trigger.current;
    if (el && el.isConnected) {
      e.preventDefault();
      el.focus({ preventScroll: true });
    }
  }, []);
  const play = useCallback((v: HomeVideo) => {
    trigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setVideo(v);
    setOpen(true);
  }, []);
  const value = useMemo(() => ({ play, open }), [play, open]);
  const short = video?.kind === "short";

  const body = video && open && (
    <div className="flex flex-col gap-4">
      <div
        className="mx-auto w-full overflow-hidden rounded-ds-lg bg-night"
        style={
          short
            ? mobile
              ? { height: "min(520px, calc(100dvh - 230px))", aspectRatio: "9 / 16", width: "auto" }
              : { aspectRatio: "9 / 16" }
            : { aspectRatio: "16 / 9" }
        }
      >
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&playsinline=1`}
          title={video.title}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          className="h-full w-full"
        />
      </div>
      <div>
        <p className="font-display text-[17px] font-bold leading-snug text-maroon-500">{video.title}</p>
        <p className="mt-1 text-[14px] text-text-secondary">{formatViewsAr(video.views)} مشاهدة</p>
      </div>
      <div className="flex gap-3">
        <a
          href={`https://www.youtube.com/${short ? "shorts/" : "watch?v="}${video.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className={btn + " bg-primary text-primary-foreground hover:bg-primary-hover"}
        >
          مشاهدة على يوتيوب ↖
        </a>
        <button type="button" onClick={() => setOpen(false)} className={btn + " bg-cream-100 text-text-primary hover:bg-cream-300"}>
          إغلاق
        </button>
      </div>
    </div>
  );

  return (
    <PlayerCtx.Provider value={value}>
      {children}
      {mobile ? (
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent aria-describedby={undefined} onCloseAutoFocus={restoreFocus} className="px-5 pb-[max(20px,env(safe-area-inset-bottom))] pt-3">
            <DrawerTitle className="sr-only">{video?.title ?? "فيديو"}</DrawerTitle>
            {body}
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            aria-describedby={undefined}
            onCloseAutoFocus={restoreFocus}
            className="pt-16"
            style={short ? { maxWidth: "min(380px, calc((100dvh - 200px) * 9 / 16))" } : { maxWidth: "min(860px, 92vw)" }}
          >
            <DialogTitle className="sr-only">{video?.title ?? "فيديو"}</DialogTitle>
            {body}
          </DialogContent>
        </Dialog>
      )}
    </PlayerCtx.Provider>
  );
}
